import { Link } from "react-router-dom";
import { Newspaper, FileSearch, Briefcase, Handshake, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const VALUES = [
  { title: "Technology-driven", desc: "We cover the enterprise stack with depth and rigour." },
  { title: "Data-driven", desc: "Every insight is grounded in research and evidence." },
  { title: "Trustworthy", desc: "Editorial independence and enterprise-grade privacy." },
  { title: "Conversion-focused", desc: "We connect audiences with the solutions they seek." },
];

const LAYERS = [
  { icon: Newspaper, title: "Content", desc: "Technology and business journalism that builds audience." },
  { icon: FileSearch, title: "Research", desc: "Gated reports and surveys that generate qualified leads." },
  { icon: Briefcase, title: "Business Solutions", desc: "Commercial programs that drive buyer inquiries." },
  { icon: Handshake, title: "Partnerships", desc: "Sponsored content, advertising and performance marketing." },
];

const About = () => {
  return (
    <div data-testid="about-page">
      <section className="relative bg-navy text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20">
          <span className="overline-label text-electric">About Lead ITES</span>
          <h1 className="mt-3 font-display text-4xl lg:text-6xl font-extrabold max-w-3xl leading-tight">
            Insights, intelligence and opportunities for modern businesses
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl text-lg">
            Lead ITES is a modern B2B technology media, research and lead-generation platform. We
            connect technology, business and growth — helping enterprises make smarter decisions
            and helping brands reach the people who make them.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16">
        <h2 className="font-display text-3xl font-extrabold text-navy">How the platform works</h2>
        <p className="mt-3 text-muted-foreground max-w-2xl">Four connected layers that turn attention into opportunity.</p>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {LAYERS.map((l) => (
            <div key={l.title} className="bg-white border border-border rounded-xl p-7 card-lift" data-testid={`about-layer-${l.title.replace(/\s/g, "-").toLowerCase()}`}>
              <div className="h-11 w-11 rounded-lg bg-navy flex items-center justify-center">
                <l.icon className="h-5 w-5 text-electric" />
              </div>
              <h3 className="mt-5 font-display font-bold text-navy text-lg">{l.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{l.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-secondary/60 py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="font-display text-3xl font-extrabold text-navy">What we stand for</h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white border border-border rounded-xl p-6">
                <div className="h-1.5 w-10 bg-electric rounded-full" />
                <h3 className="mt-4 font-display font-bold text-navy">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16">
        <div className="rounded-2xl bg-navy text-white p-10 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-3xl font-extrabold">Let's build something together</h2>
            <p className="mt-3 text-white/75">Explore insights, unlock research or partner with Lead ITES.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild className="btn-electric rounded-full px-6 h-11"><Link to="/articles">Explore Insights <ArrowRight className="h-4 w-4" /></Link></Button>
            <Button asChild variant="outline" className="rounded-full px-6 h-11 bg-transparent border-white/30 text-white hover:bg-white hover:text-navy"><Link to="/contact">Contact Us</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
